import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import api from "../utils/api";

const CertsAndSecrets = () => {
  const [items, setItems] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fixingDeleted, setFixingDeleted] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
    appName: "",
  });
  const [subscriptionId, setSubscriptionId] = useState("");
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        configId: selectedConfigId,
        ...filters,
        ...(subscriptionId ? { subscriptionId } : {}),
      };

      const response = await api.get("/api/certs-secrets", {
        params: { ...params, subscriptionId: subscriptionId || null },
      });

      setItems(response.data.items || []);
      setConfigs(response.data.configs || []);

      if (response.data.selectedConfigId && !selectedConfigId) {
        setSelectedConfigId(response.data.selectedConfigId);
      }

      const summaryResponse = await api.get("/api/certs-secrets/summary", {
        params: {
          configId: selectedConfigId || response.data.selectedConfigId,
        },
      });
      setSummary(summaryResponse.data);

      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      console.error("Failed to fetch certs and secrets:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load certificates and secrets. Please ensure an Azure tenant is configured in Settings.",
      );
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedConfigId, filters, subscriptionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefreshFromEntra = useCallback(async () => {
    try {
      setRefreshing(true);
      setError("");
      await api.post("/api/certs-secrets/sync", {
        configId: selectedConfigId || undefined,
        subscriptionId: subscriptionId || undefined,
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to sync from Entra:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to sync from Entra/Azure. Please try again.",
      );
    } finally {
      setRefreshing(false);
    }
  }, [selectedConfigId, subscriptionId, fetchData]);

  const handleFixDeleted = useCallback(async () => {
    try {
      setFixingDeleted(true);
      setError("");
      const { data } = await api.post("/api/certs-secrets/fix-deleted", {
        configId: selectedConfigId || undefined,
      });
      await fetchData();
      if (data?.rowsUpdated > 0) {
        setError("");
      }
    } catch (err) {
      console.error("Failed to fix deleted status:", err);
      const status = err.response?.status;
      const data = err.response?.data;
      let message =
        data?.message || data?.error || "Failed to fix deleted status.";
      if (status === 404) {
        message =
          "Endpoint not found (404). Restart the API server so the fix-deleted route is loaded. If the app uses a different API URL, ensure it points to the running server (e.g. PORT=5001).";
      } else if (status !== 403) {
        message +=
          " Ensure Application.Read.All permission is granted and admin consent is done.";
      }
      setError(message);
    } finally {
      setFixingDeleted(false);
    }
  }, [selectedConfigId, fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleConfigChange = (configId) => {
    setSelectedConfigId(configId ? parseInt(configId) : null);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "expiring_soon":
        return "warning";
      case "expired":
        return "error";
      case "deleted":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "expiring_soon":
        return "Expiring Soon";
      case "expired":
        return "Expired";
      case "deleted":
        return "Deleted";
      default:
        return "Unknown";
    }
  };

  const exportToCSV = () => {
    try {
      const headers = [
        "Type",
        "App Name",
        "App ID",
        "Display Name",
        "Expiry Date",
        "Days Until Expiry",
        "Status",
      ];

      const csvData = items.map((item) => [
        item.type === "certificate" ? "Certificate" : "Secret",
        item.appName || "",
        item.appId || "",
        item.displayName || "",
        item.expiryDate
          ? format(new Date(item.expiryDate), "dd/MM/yyyy")
          : "No expiry",
        item.daysUntilExpiry !== null ? item.daysUntilExpiry : "N/A",
        getStatusLabel(item.status),
      ]);

      const csvContent = [headers, ...csvData]
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `certs_secrets_export_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export failed:", error);
      setError("Failed to export data");
    }
  };

  const columns = [
    {
      field: "type",
      headerName: "Type",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          icon={
            params.value === "certificate" ? <SecurityIcon /> : <VpnKeyIcon />
          }
          label={params.value === "certificate" ? "Certificate" : "Secret"}
          color={params.value === "certificate" ? "primary" : "secondary"}
          size="small"
        />
      ),
    },
    {
      field: "appName",
      headerName: "Application",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "appId",
      headerName: "App ID",
      flex: 1.2,
      minWidth: 280,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          fontSize="0.875rem"
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "displayName",
      headerName: "Display Name",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Typography variant="body2" color="textSecondary">
              No expiry
            </Typography>
          );
        }
        const expiryDate = new Date(params.value);
        const isExpired = expiryDate < new Date();
        return (
          <Typography
            variant="body2"
            color={isExpired ? "error" : "textPrimary"}
            fontWeight={isExpired ? "bold" : "normal"}
          >
            {format(expiryDate, "dd MMM yyyy")}
          </Typography>
        );
      },
    },
    {
      field: "daysUntilExpiry",
      headerName: "Days Until Expiry",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        if (params.value === null) {
          return (
            <Typography variant="body2" color="textSecondary">
              N/A
            </Typography>
          );
        }
        const days = params.value;
        const color =
          days < 0 ? "error" : days <= 30 ? "warning.main" : "textPrimary";
        return (
          <Typography
            variant="body2"
            color={color}
            fontWeight={days <= 30 ? "bold" : "normal"}
          >
            {days < 0 ? `${Math.abs(days)} days ago` : `${days} days`}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
  ];

  // Filter items for pagination (client-side since we get all items from API)
  const filteredItems = items;
  const paginatedItems = filteredItems.slice(
    pagination.page * pagination.pageSize,
    (pagination.page + 1) * pagination.pageSize,
  );

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
            disabled={loading || items.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshFromEntra}
            disabled={refreshing || loading}
          >
            {refreshing ? "Syncing from Entra..." : "Refresh from Entra"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleFixDeleted}
            disabled={fixingDeleted || loading}
          >
            {fixingDeleted ? "Fixing..." : "Fix deleted status"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          style={{ marginBottom: 16 }}
        >
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Items
                </Typography>
                <Typography variant="h4">{summary.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Certificates
                </Typography>
                <Typography variant="h4" color="primary">
                  {summary.certificates}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Secrets
                </Typography>
                <Typography variant="h4" color="secondary">
                  {summary.secrets}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expired
                </Typography>
                <Typography variant="h4" color="error">
                  {summary.expired || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expiring Soon
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {summary.expiringSoon}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            {configs.length > 1 && (
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Azure Tenant</InputLabel>
                  <Select
                    value={selectedConfigId || ""}
                    onChange={(e) => handleConfigChange(e.target.value)}
                    label="Azure Tenant"
                  >
                    {configs.map((config) => (
                      <MenuItem key={config.id} value={config.id}>
                        {config.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Subscription ID (for App Service certs)"
                value={subscriptionId}
                onChange={(e) => setSubscriptionId(e.target.value)}
                placeholder="e.g. 39d45825-a3ad-4103-ba10-c68cdab425a0"
                helperText="Optional: Leave empty to fetch from all subscriptions, or enter a specific subscription ID"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Search by app name or display name"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="certificate">Certificates</MenuItem>
                  <MenuItem value="secret">Secrets</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="expiring_soon">Expiring Soon</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="App Name"
                value={filters.appName}
                onChange={(e) => handleFilterChange("appName", e.target.value)}
                placeholder="Filter by app"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Alert severity="info">
              {error
                ? "No certificates or secrets found. Please check your Azure tenant configuration."
                : "No certificates or secrets found."}
            </Alert>
          ) : (
            <DataGrid
              rows={paginatedItems}
              columns={columns}
              pageSize={pagination.pageSize}
              rowsPerPageOptions={[10, 20, 50, 100]}
              pagination
              autoHeight
              disableSelectionOnClick
              getRowId={(row) => `${row.type}-${row.id}`}
              onPageChange={(newPage) =>
                setPagination((prev) => ({ ...prev, page: newPage }))
              }
              onPageSizeChange={(newPageSize) =>
                setPagination((prev) => ({
                  ...prev,
                  pageSize: newPageSize,
                  page: 0,
                }))
              }
              initialState={{
                pagination: {
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                },
              }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CertsAndSecrets;
